import { useState, useEffect, useRef } from "react";
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  useTheme,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RefreshIcon from "@mui/icons-material/Refresh";
import VideocamIcon from "@mui/icons-material/Videocam";
import * as Sentry from "@sentry/react";
import axios from "axios";

/**
 * VideoProcessor Component
 * 
 * Handles AI processing of a single video blob with retry capability.
 * Merges the rich UI/Retry logic from Feature file with Axios/Sentry from Latest file.
 */
const VideoProcessor = ({ videoBlob, videoId, companyData, onResult, onError, selectedInterests, otherInterest, additionalNotes }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  
  const API_BASE = import.meta.env.VITE_API_BASE;
  const processingStarted = useRef(false);
  const progressInterval = useRef(null);

  // Simulate progress for better UX (From Feature File - Smoother logic)
  useEffect(() => {
    if (loading && !error) {
      progressInterval.current = setInterval(() => {
        setProgress((prevProgress) => {
          // Don't go to 100% until actually done
          if (prevProgress >= 90) return 90;
          // Slower progress as we approach 90%
          const increment = prevProgress > 70 ? Math.random() * 5 : Math.random() * 10;
          return Math.min(prevProgress + increment, 90);
        });
      }, 500);

      return () => {
        if (progressInterval.current) {
          clearInterval(progressInterval.current);
        }
      };
    }
  }, [loading, error]);

  /**
   * Upload video to server and trigger AI processing
   * Uses Axios and Sentry from Latest File, but logs videoId from Feature File
   */
  const uploadVideoToServerAndHandleGeminiProcessing = async (blob) => {
    console.log(`🎬 Starting AI processing for video: ${videoId} (Attempt ${retryCount + 1})`);
    
    const formData = new FormData();
    formData.append(
      "video",
      new File([blob], "video.webm", { type: "video/webm" })
    );
    if (selectedInterests && selectedInterests.length > 0) {
      formData.append("interests", JSON.stringify(selectedInterests));
    }
    if (otherInterest) {
      formData.append("otherInterest", otherInterest);
    }
    if (additionalNotes) {
      formData.append("additionalNotes", additionalNotes);
    }

    try {
      const response = await axios.post(
        `${API_BASE}/parse-video-save-cards`,
        formData,
        {
          withCredentials: true,
          // Do NOT set Content-Type to multipart/form-data. axios will handle it.
        }
      );
      const data = response.data;

      if (!data.success) {
        // Log to Sentry if server returned a genuine processing/internal error
        Sentry.captureException(
          new Error(data.message || "Video processing failed"),
          {
            tags: {
              section: "video-processor",
              source: "parse-video-save-cards",
            },
            extra: { response: data, videoId },
          }
        );
        throw new Error(data.message || "Failed to upload video to server");
      }

      console.log(`✅ Processing successful for ${videoId}:`, data?.cards?.length || 0, "cards found");
      return data?.cards || [];

    } catch (err) {
      // Determine error message based on Axios response
      let errorMessage = "Failed to upload video. Please try again.";
      
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Only log server/infra errors (>=500) to Sentry
      if (err.response && err.response.status >= 500) {
        Sentry.captureException(err, {
          tags: {
            section: "video-processor",
            source: "parse-video-save-cards",
          },
          extra: {
            status: err.response.status,
            response: err.response.data,
            videoId
          },
        });
      }
      
      console.error(`❌ Error processing ${videoId}:`, err);
      throw new Error(errorMessage); // Throw normalized error message
    }
  };

  /**
   * Main processing effect
   * Runs once when component mounts (or retry changes)
   */
  useEffect(() => {
    // Prevent double-processing in development mode (React strict mode)
    if (processingStarted.current) {
      return;
    }
    processingStarted.current = true;

    const handleVideoProcessing = async () => {
      if (!videoBlob) {
        const msg = "No video provided";
        setError(msg);
        setLoading(false);
        if (onError) onError(msg);
        return;
      }

      setLoading(true);
      setError("");
      setProgress(0);

      try {
        // Process the video
        const cards = await uploadVideoToServerAndHandleGeminiProcessing(videoBlob);

        // Complete the progress bar
        setProgress(100);

        // Small delay to show 100% completion before hiding
        setTimeout(() => {
          setLoading(false);
          onResult(cards);
        }, 500);

      } catch (uploadErr) {
        const errorMessage = uploadErr.message || "Failed to upload video.";
        setError(errorMessage);
        setLoading(false);
        if (onError) onError(errorMessage);
      }
    };

    handleVideoProcessing();

    // Cleanup
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
    // eslint-disable-next-line
  }, []); 

  /**
   * Retry handler
   * Called when user clicks the retry button (Feature File Logic)
   */
  const handleRetry = async () => {
    console.log(`🔄 Retrying processing for video: ${videoId}`);
    
    setRetryCount(prev => prev + 1);
    setError("");
    setLoading(true);
    setProgress(0);
    processingStarted.current = false; // Reset ref to allow processing again

    try {
      const cards = await uploadVideoToServerAndHandleGeminiProcessing(videoBlob);
      setProgress(100);
      
      setTimeout(() => {
        setLoading(false);
        onResult(cards);
      }, 500);

    } catch (err) {
      const errorMessage = err.message || "Failed to process video.";
      setError(errorMessage);
      setLoading(false);
      if (onError) onError(errorMessage);
    }
  };

  // ============== LOADING STATE (Rich UI from Feature File) ==============
  if (loading) {
    return (
      <Fade in timeout={600}>
        <Card
          elevation={3}
          sx={{
            maxWidth: 450,
            mx: "auto",
            mt: 3,
            borderRadius: 4,
            backgroundColor: "#FAFAFA",
            boxShadow: "0 8px 24px rgba(99, 102, 241, 0.1)",
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            {/* Header with icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                backgroundColor: "#6366F1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
              }}
            >
              <SmartToyIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1F2937",
                mb: 1,
              }}
            >
              AI Processing Video
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                mb: 3,
                lineHeight: 1.5,
              }}
            >
              Our AI is analyzing your video to extract business card
              information
            </Typography>

            {/* Progress Section */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <VideocamIcon sx={{ color: "#6366F1", mr: 1, fontSize: 20 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                  }}
                >
                  Processing Progress: {Math.round(progress)}%
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#E5E7EB",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#6366F1",
                    borderRadius: 4,
                  },
                }}
              />
            </Box>

            {/* Animated spinner */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <CircularProgress
                size={40}
                sx={{
                  color: "#6366F1",
                  "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                  },
                }}
              />
            </Box>

            <Typography
              variant="body2"
              sx={{
                color: "#9CA3AF",
                fontStyle: "italic",
              }}
            >
              This may take a few moments...
            </Typography>

            {retryCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "#6B7280",
                }}
              >
                Retry attempt {retryCount}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Fade>
    );
  }

  // ============== ERROR STATE (Rich UI from Feature File) ==============
  if (error) {
    return (
      <Fade in timeout={600}>
        <Card
          elevation={3}
          sx={{
            maxWidth: 450,
            mx: "auto",
            mt: 3,
            borderRadius: 4,
            backgroundColor: "#FAFAFA",
            boxShadow: "0 8px 24px rgba(239, 68, 68, 0.1)",
          }}
        >
          <CardContent sx={{ p: 4, textAlign: "center" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 3,
                backgroundColor: "#EF4444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
              }}
            >
              <Typography sx={{ fontSize: 28 }}>⚠️</Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1F2937",
                mb: 2,
              }}
            >
              Processing Failed
            </Typography>

            <Alert
              severity="error"
              sx={{
                borderRadius: 3,
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                border: "none",
                mb: 2,
                textAlign: "left",
                "& .MuiAlert-icon": {
                  color: "#DC2626",
                },
              }}
            >
              {error}
            </Alert>

            <Typography
              variant="body2"
              sx={{
                color: "#6B7280",
                mb: 3,
              }}
            >
              This video failed to process. You can retry processing this video,
              or delete it and record a new one.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              fullWidth
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                py: 1.5,
                backgroundColor: "#6366F1",
                "&:hover": { backgroundColor: "#4F46E5" },
              }}
            >
              Retry Processing This Video
            </Button>

            {retryCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 2,
                  color: "#9CA3AF",
                }}
              >
                Previous retry attempts: {retryCount}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Fade>
    );
  }

  // ============== SUCCESS STATE ==============
  // When processing is complete, this component returns null
  // and the parent component displays the results
  return null;
};

export default VideoProcessor;